/**
 * Service API pour les fonctionnalités de matching et correspondances (Module Navette)
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

// Configuration axios avec authentification
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token à chaque requête
api.interceptors.request.use(config => {
  const authHeader = getAuthHeader();
  config.headers = { ...config.headers, ...authHeader };
  return config;
});

// ==================== ANALYSE SÉMANTIQUE NLP ====================

/**
 * Analyse sémantique entre certifications et formations via NLP
 * @param {string} certificationId - ID de la certification source
 * @param {string} formationId - ID de la formation cible (optionnel)
 * @param {number} threshold - Seuil de similarité (0-1)
 * @param {number} limit - Nombre max de résultats
 */
export const semanticAnalysis = async (certificationId, formationId = null, threshold = 0.6, limit = 50) => {
  const response = await api.post('/matching/semantic-analysis', {
    certificationId,
    formationId,
    threshold,
    limit
  });
  return response.data;
};

// ==================== GRAPHE INTER-RÉFÉRENTIELS ====================

/**
 * Génère un graphe de correspondances entre référentiels
 * @param {string} referentialTypes - Types de référentiels (certifications, formations)
 * @param {number} minConnections - Nombre minimum de connexions
 * @param {string} focusEntity - ID d'entité pour focus (optionnel)
 */
export const getInterReferentialGraph = async (referentialTypes = 'certifications,formations', minConnections = 1, focusEntity = null) => {
  const params = { referentialTypes, minConnections };
  if (focusEntity) {
    params.focusEntity = focusEntity;
  }

  const response = await api.get('/matching/inter-referential-graph', { params });
  return response.data;
};

// ==================== FILTRES MULTICRITÈRES ====================

/**
 * Filtre des compétences selon plusieurs critères
 * @param {Object} filters - Filtres à appliquer
 * @param {Array} filters.sectors - Secteurs d'activité
 * @param {Array} filters.levels - Niveaux de qualification
 * @param {Array} filters.keywords - Mots-clés
 * @param {boolean} filters.transversal - Compétences transversales uniquement
 * @param {string} groupBy - Groupement (sector, level, type)
 */
export const filterCompetences = async (filters = {}, groupBy = 'sector') => {
  const response = await api.post('/matching/filter-competences', {
    filters,
    groupBy
  });
  return response.data;
};

// ==================== EXPORT CORRESPONDANCES ====================

/**
 * Exporte les correspondances entre référentiels
 * @param {string} format - Format d'export (csv ou json)
 * @param {string} entityType - Type d'entité (certifications, formations, all)
 * @param {Array} entityIds - IDs spécifiques à exporter (optionnel)
 * @param {boolean} includeDetails - Inclure détails complets
 */
export const exportCorrespondences = async (format = 'csv', entityType = 'all', entityIds = null, includeDetails = true) => {
  const params = {
    format,
    entityType,
    includeDetails: includeDetails.toString()
  };

  if (entityIds && entityIds.length > 0) {
    params.entityIds = entityIds.join(',');
  }

  const response = await api.get('/matching/export-correspondences', {
    params,
    responseType: 'blob'
  });

  // Créer un lien de téléchargement
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `correspondances_${entityType}_${Date.now()}.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, message: 'Export réussi' };
};

// ==================== ASSISTANT IA ====================

/**
 * Assistant IA pour validation et suggestions de correspondances
 * @param {string} action - Action demandée (suggest, validate, explain)
 * @param {Object} context - Contexte de l'action
 * @param {string} context.certificationId - ID certification
 * @param {string} context.formationId - ID formation (optionnel)
 * @param {Array} context.competenceIds - IDs compétences (optionnel)
 */
export const aiAssistant = async (action, context) => {
  const response = await api.post('/matching/ai-assistant', {
    action,
    context
  });
  return response.data;
};

/**
 * Suggestions de correspondances pour une certification
 * @param {string} certificationId - ID de la certification
 * @param {number} limit - Nombre de suggestions
 */
export const suggestMatches = async (certificationId, limit = 10) => {
  return aiAssistant('suggest', { certificationId, limit });
};

/**
 * Validation d'une correspondance certification-formation
 * @param {string} certificationId - ID de la certification
 * @param {string} formationId - ID de la formation
 */
export const validateMatch = async (certificationId, formationId) => {
  return aiAssistant('validate', { certificationId, formationId });
};

/**
 * Explication d'une correspondance
 * @param {string} certificationId - ID de la certification
 * @param {string} formationId - ID de la formation
 */
export const explainMatch = async (certificationId, formationId) => {
  return aiAssistant('explain', { certificationId, formationId });
};

export default {
  semanticAnalysis,
  getInterReferentialGraph,
  filterCompetences,
  exportCorrespondences,
  aiAssistant,
  suggestMatches,
  validateMatch,
  explainMatch
};
